<template>
    <div class="trip-leg-summary" :class="cssClasses">
        <div class="trip-leg-summary__item-container">
            <div class="trip-leg-summary__item-container-inner">
                <div
                    v-for="leg in legs"
                    :key="leg.id"
                    v-intersect="(isIntersecting: boolean) => onIntersect(leg.id, isIntersecting)"
                    :data-id="leg.id"
                    class="trip-leg-summary__item"
                >
                    <div class="trip-leg-summary__content">
                        <template v-if="leg.tripMode === TripMode.Foot">
                            <transfer-leg :icon="leg.icon" :color="leg.color" :duration="leg.duration" :hide-duration="hideLegDuration" />
                            <div class="trip-leg-summary__duration">
                                <stop-time-tooltip
                                    v-if="!hideStopTime && legs.length === 1"
                                    :expected-time="leg.expectedStartTime"
                                    :expected-time-is-realtime="leg.realtime"
                                    :aimed-time="leg.aimedStartTime"
                                >
                                    <div class="d-flex align-center line-height-1">
                                        <realtime-indicator v-if="leg.realtime && leg.isStartingSoon" class="mr-1" />
                                        <span :class="{ 'font-weight-medium': leg.realtime && leg.isStartingSoon }">
                                            {{ startTime(leg) }}
                                        </span>
                                    </div>
                                </stop-time-tooltip>
                            </div>
                        </template>
                        <template v-else>
                            <mode-chip
                                :color="leg.color"
                                :icon="leg.icon"
                                :icon-color="leg.iconColor"
                                icon-size="28"
                                :title="getLegTitle(leg)"
                                :is-compact="getIsCompact(leg)"
                                :notification-messages="leg.notificationMessages"
                                :active-summary="activeSummary"
                            />
                            <div class="trip-leg-summary__duration">
                                <stop-time-tooltip
                                    v-if="!hideStopTime && visibilityMap[leg.id]"
                                    :expected-time="leg.expectedStartTime"
                                    :expected-time-is-realtime="leg.realtime"
                                    :aimed-time="leg.aimedStartTime"
                                >
                                    <div class="d-flex align-center line-height-1">
                                        <realtime-indicator v-if="leg.realtime && leg.isStartingSoon" class="mr-1" />
                                        <span :class="{ 'font-weight-medium': leg.realtime && leg.isStartingSoon }">
                                            {{ startTime(leg) }}
                                        </span>
                                    </div>
                                </stop-time-tooltip>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <v-spacer />
        <div class="trip-leg-summary__item trip-leg-summary__destination">
            <div class="trip-leg-summary__content">
                <v-sheet rounded border="sm" width="32" height="30" class="d-flex align-center justify-center">
                    <v-icon icon="mdi-map-marker" size="20" color="grey-darken-1" />
                </v-sheet>
                <div v-if="!hideStopTime" class="trip-leg-summary__duration text-body-1 text-grey-darken-5">
                    <realtime-indicator v-if="lastLeg!.realtime && lastLeg!.isEndingSoon" class="mr-1" />
                    <span class="line-height-1">{{ endTime(lastLeg!) }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ModeChip, RealtimeIndicator, StopTimeTooltip } from '@/features/common/components';
import { getTripModeIcon } from '@/features/common/composables';
import { toLocaleTimeString } from '@geta/kolumbus-frontend/composables';
import { LegDto, TripMode } from '@/types/webapi';
import { getTransferTime, isTransitMode } from '@/features/trips/helpers';
import { last } from 'lodash-es';
import TransferLeg from './TransferLeg.vue';
import { computed, reactive } from 'vue';
import { SHORT_TRANSFER_THRESHOLD_IN_SECONDS } from '@/features/common/constants';

interface Props {
    /** Array of trip leg data to display */
    modelValue?: LegDto[];

    /** Controls how compact the leg summary display is */
    variant?: 'collapsed' | 'auto' | 'expanded';

    /** Whether this summary is currently active/selected */
    activeSummary?: boolean;

    /** Whether to hide the stop time */
    hideStopTime?: boolean;

    /** Whether to hide the leg duration */
    hideLegDuration?: boolean;

    /** Whether to wrap the leg summary in a flex container */
    noWrap?: boolean;
}

const props = withDefaults(defineProps<Props>(), { modelValue: () => [], variant: 'auto' });
const cssClasses = computed(() => ({ 'trip-leg-summary--no-wrap': props.noWrap }));
const lastLeg = computed(() => last(props.modelValue));
const visibilityMap = reactive<Record<string, boolean>>({});

const startTime = computed(() => (leg: LegDto) => toLocaleTimeString(leg.isStartingSoon ? leg.expectedStartTime : leg.aimedStartTime));
const endTime = computed(() => (leg: LegDto) => toLocaleTimeString(leg.isEndingSoon ? leg.expectedEndTime : leg.aimedEndTime));

function getLegTitle(leg: LegDto) {
    return isTransitMode(leg.tripMode) ? leg.lineName : undefined;
}

function getIsCompact(leg: LegDto) {
    if (props.variant === 'collapsed') return true;
    if (props.variant === 'expanded') return false;

    return props.modelValue.length > 5 && !isTransitMode(leg.tripMode);
}

const legs = computed(() =>
    props.modelValue.reduce<(LegDto & { icon?: string; color?: string; iconColor?: string })[]>((acc, leg, index) => {
        const isWalkingLeg = leg.tripMode === TripMode.Foot;
        const isTransferLeg = isWalkingLeg && index > 0 && index < props.modelValue.length - 1;

        if (isTransferLeg) {
            const previousLeg = props.modelValue[index - 1];
            const nextLeg = props.modelValue[index + 1];

            const transferTime = getTransferTime(previousLeg, nextLeg, leg);
            // set warning color if time left is less than threshold
            const iconColor = transferTime < SHORT_TRANSFER_THRESHOLD_IN_SECONDS ? 'yellow-darken-1' : 'grey-darken-1';

            // if leg duration is more than the walking transfer threshold, show the leg as is
            if (leg.duration > SHORT_TRANSFER_THRESHOLD_IN_SECONDS) {
                return [...acc, { ...leg, color: iconColor }];
            }

            // else override duration and color
            return [
                ...acc,
                {
                    ...leg,
                    duration: transferTime,
                    icon: 'kdi-arrow-left-right',
                    iconColor
                }
            ];
        }

        return [
            ...acc,
            {
                ...leg,
                color: leg.tripMode === TripMode.Foot || leg.tripMode === TripMode.Bicycle ? undefined : 'grey-darken-5',
                icon: getTripModeIcon(leg.tripMode, leg.transitInfo),
                iconColor: leg.tripMode === TripMode.Foot || leg.tripMode === TripMode.Bicycle ? 'grey-darken-1' : undefined
            }
        ];
    }, [])
);

function onIntersect(legId: string, isIntersecting: boolean) {
    visibilityMap[legId] = isIntersecting;
}
</script>

<style lang="scss">
@use 'vuetify/settings' as *;
@use 'vuetify/lib/styles/tools/index' as tools;

.trip-leg-summary {
    --trip-leg-summary-line-color: var(--v-color-neutral-200);

    position: relative;
    display: flex;
    flex-direction: row;
    align-items: flex-end;

    &--no-wrap {
        flex-wrap: nowrap;
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 10px;
        right: 10px;
        height: 100%;

        /* Line color */
        background-color: var(--trip-leg-summary-line-color);

        /* Use mask to create dashed horizontal lines that repeat every 64px vertically,
           with a 2px line positioned 24px from the top of each row. */
        mask-image:
            repeating-linear-gradient(to right, #000 0 5px, transparent 5px 10px),
            repeating-linear-gradient(to bottom, transparent 0 24px, #000 24px 26px, transparent 26px 66px);
        /* Size: first mask is a 2px-high band; second spans full width with 64px tile */
        mask-size:
            auto 2px,
            100% 66px;
        mask-position: 0 -2px;
        mask-repeat: repeat, repeat;
        mask-composite: intersect;
    }

    &__item {
        display: flex;
        align-items: flex-start;
        position: relative;
        padding-top: $spacer * 2;

        &:not(.trip-leg-summary__destination) {
            padding-left: 6px;
            padding-right: 6px;
        }

        &-container {
            flex-shrink: 1;
            flex-grow: 0;
            margin: 0 -6px;

            &-inner {
                display: flex;
                flex-wrap: wrap;

                .trip-leg-summary--no-wrap & {
                    flex-wrap: nowrap;
                    padding-right: 40px; // spacer so summary items do not overlap with destination
                }
            }

            .trip-leg-summary--no-wrap & {
                overflow: hidden;
                mask-image: linear-gradient(to right, black calc(100% - #{$spacer * 6}), transparent);
                padding-right: $spacer * 6;
            }

            .trip-leg-summary__item:last-child {
                padding-right: 40px;
            }
        }
    }

    &__duration {
        $-font-size: tools.map-deep-get($typography, 'body-1', 'size');
        $-letter-spacing: tools.map-deep-get($typography, 'body-1', 'letter-spacing');
        $-line-height: tools.map-deep-get($typography, 'body-1', 'line-height');
        $-text-transform: tools.map-deep-get($typography, 'body-1', 'text-transform');

        white-space: nowrap;
        line-height: 1;
        padding-top: $spacer * 2;
        display: flex;
        align-items: center;
        @include tools.typography($-font-size, 400, $-letter-spacing, $-line-height, $-text-transform);
    }

    &__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        height: 58px;
    }

    &__destination {
        flex-shrink: 0;
        flex-grow: 0;

        .trip-leg-summary:not(.trip-leg-summary--no-wrap) & {
            position: absolute;
            right: 0;
        }
    }
}
</style>
